import styled from "styled-components";
import { theme } from "../../../theme";
import { HighligtActionButton } from "../../atoms/SlideActionPanel";
import { AddIcon } from "../../atoms/icons/AddIcon";
import { useTranslation } from "react-i18next";
import BodySmall from "../../atoms/typography/BodySmall";
import { TagSlidePanel } from "../../molecules/invoices/InvoiceToolbarPanels";
import SlideActionToolbar from "../../molecules/SlideActionToolbar";
import SendInvoiceMessageToClientButton from "../../molecules/invoices/SendInvoiceMessageToClientButton";
import { CommentIcon } from "../../atoms/icons/CommentIcon";
import { useRejectInvoicesFlow } from "../../molecules/invoices/useRejectInvoicesFlow";
import { CrossIcon } from "../../atoms/icons/CrossIcon";
import {
  InvoiceValidationTags,
  type InvoiceTag,
} from "../../../models/InvoiceTags";
import { getInvoiceTagTitleKey } from "../../../utils/invoicesHelpers";
import DeleteInvoicesButton from "../../molecules/invoices/DeleteInvoicesButton";
import { TrashIcon } from "../../atoms/icons/TrashIcon";
import { RefreshIcon } from "../../atoms/icons/RefreshIcon";
import { SendIcon } from "../../atoms/icons/SendIcon";
import { useSaveSections } from "./SaveSectionsContext";
import { SaveIconMedium } from "../../atoms/icons/SaveIcon";

interface Props {
  invoiceId: string;
  clientIdentifier: string;
  invoiceTags: Array<InvoiceTag>;
  isValid: boolean;
  onRevalidate: () => void;
  revalidateLoading?: boolean;
  onSendToAssessment: () => void;
  sendToAssessmentLoading?: boolean;
  onApplyTags: (add: string[], remove: string[]) => Promise<void>;
}

const ActionBottomBarContainer = styled.div`
  display: flex;
  padding: 16px;
  background-color: ${theme.colors.secondary20};
  position: fixed;
  bottom: 0;
  left: var(--sidebar-width, 0px);
  right: 0;
`;

function resolveTagSelectionState(invoiceTags: Array<InvoiceTag>) {
  return InvoiceValidationTags.map((tag) => ({
    key: tag,
    labelKey: getInvoiceTagTitleKey(tag),
    selected: invoiceTags.includes(tag),
  }));
}

const ValidationActionBottomBar: React.FC<Props> = ({
  invoiceId,
  clientIdentifier,
  invoiceTags,
  isValid,
  onRevalidate,
  revalidateLoading,
  onSendToAssessment,
  sendToAssessmentLoading,
  onApplyTags,
}) => {
  const { t } = useTranslation();

  const { openModal: openRejectModal, modal: rejectModal } =
    useRejectInvoicesFlow({
      type: "single",
      invoiceId: invoiceId,
    });

  const dirtySections = useSaveSections().filter((s) => s.isDirty);

  return (
    <ActionBottomBarContainer>
      {rejectModal}
      <SlideActionToolbar
        actions={(openPanel, closePanel) => (
          <>
            <HighligtActionButton onClick={() => openPanel("tag")}>
              <AddIcon />
              <BodySmall>{t("invoices.tag")}</BodySmall>
            </HighligtActionButton>

            <HighligtActionButton
              onClick={() => {
                closePanel();
                openRejectModal();
              }}
            >
              <CrossIcon />
              <BodySmall>{t("documentHandler.reject")}</BodySmall>
            </HighligtActionButton>

            <DeleteInvoicesButton
              invoiceIds={[invoiceId]}
              renderTrigger={(onClick) => (
                <HighligtActionButton
                  onClick={() => {
                    closePanel();
                    onClick();
                  }}
                >
                  <TrashIcon />
                  <BodySmall>{t("invoices.deleteInvoice")}</BodySmall>
                </HighligtActionButton>
              )}
            />

            <HighligtActionButton
              onClick={() => {
                closePanel();
                onRevalidate();
              }}
              disabled={revalidateLoading}
            >
              <RefreshIcon />
              <BodySmall>{t("invoices.revalidate")}</BodySmall>
            </HighligtActionButton>

            {isValid && (
              <HighligtActionButton
                onClick={() => {
                  closePanel();
                  onSendToAssessment();
                }}
                disabled={sendToAssessmentLoading}
              >
                <SendIcon />
                <BodySmall>{t("invoices.sendToAssessment")}</BodySmall>
              </HighligtActionButton>
            )}

            <SendInvoiceMessageToClientButton
              invoiceIds={[invoiceId]}
              clientIdentifier={clientIdentifier}
              renderTrigger={(onClick) => (
                <HighligtActionButton onClick={onClick}>
                  <CommentIcon />
                  <BodySmall>
                    {t("invoiceAssessment.messageToIssuer")}
                  </BodySmall>
                </HighligtActionButton>
              )}
            />
          </>
        )}
        panels={(isActivePanel, panelRef, panelDirection, closePanel) => (
          <>
            {isActivePanel("tag") && (
              <TagSlidePanel
                key={`tag-${panelDirection}`}
                panelRef={panelRef}
                panelDirection={panelDirection}
                closePanel={closePanel}
                selectedCount={1}
                tags={resolveTagSelectionState(invoiceTags)}
                onApply={onApplyTags}
              />
            )}
          </>
        )}
      />
      {dirtySections.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 24,
            marginLeft: "auto",
            alignItems: "center",
            padding: "4px 8px",
            backgroundColor: theme.colors.grey02,
            borderRadius: 8,
          }}
        >
          {dirtySections.map((section) => (
            <HighligtActionButton
              key={section.id}
              onClick={section.onSave}
              disabled={section.loading}
            >
              <SaveIconMedium />
              <BodySmall>{section.label}</BodySmall>
            </HighligtActionButton>
          ))}
        </div>
      )}
    </ActionBottomBarContainer>
  );
};

export default ValidationActionBottomBar;
